import type { Settings, BindableAction } from '../settings';
import { saveSettings } from '../settings';
import { typingInField } from './dom';

// Input state. Movement intent is camera-relative (camera-focused). Keys are resolved
// through the rebindable keybinding map in Settings, so any movement key can be remapped
// in the Esc menu. Alt (free-look) and middle-mouse (orbit) are fixed.
//
// Camera control:
//  - Middle-mouse drag orbits. Movement re-bases to follow the camera.
//  - Hold ALT for free-look: mouse aims the camera (no button) while travel stays locked.
//  - Vertical look can be inverted per source (middle-mouse / Alt) and scaled by sensitivity.

export interface InputState {
  forward: boolean;
  back: boolean;
  left: boolean;
  right: boolean;
  sprint: boolean;
  crouch: boolean;
  jump: boolean;
  leanLeft: boolean;
  leanRight: boolean;
  altHeld: boolean;
  primaryHeld: boolean; // left mouse held: swing / gather
  // Accumulated since last consume. Read once per frame, then cleared.
  orbitDX: number;
  orbitDY: number;
  zoomDelta: number;
}

export class Input {
  readonly state: InputState = {
    forward: false,
    back: false,
    left: false,
    right: false,
    sprint: false,
    crouch: false,
    jump: false,
    leanLeft: false,
    leanRight: false,
    altHeld: false,
    primaryHeld: false,
    orbitDX: 0,
    orbitDY: 0,
    zoomDelta: 0,
  };

  // Current cursor position in CSS pixels, for raycasting what the player is aiming at.
  readonly cursor = { x: 0, y: 0 };
  rightHeld = false; // right mouse held (opens the tool wheel; gun aim-down-sights)
  adsMode = false;   // set by main: a gun/bow is out -> right mouse is TAP=interact / HOLD=ADS (camera keeps moving)
  private rightGunPress = false; // the current right-press began with a gun/bow out (tap vs hold applies)
  private rightDownAt = 0;        // performance.now() of the current right-press (to split tap vs hold)
  private static readonly RIGHT_TAP_MS = 150; // right-hold beyond this = aim-down-sights; a quicker tap = interact/pickup
  get aiming(): boolean { return this.rightGunPress && this.rightHeld && performance.now() - this.rightDownAt > Input.RIGHT_TAP_MS; } // aiming down sights (gun out + right held past the tap window)

  onDebugToggle: (() => void) | null = null;
  onMenuToggle: (() => void) | null = null;
  onInteract: (() => void) | null = null;
  // HOLD-vs-TAP (#7), OPT-IN: if a hold handler is set, the tap fires on RELEASE (so it can be told apart from a hold)
  // and the hold fires once at HOLD_MS while still pressed. The handler RETURNS whether it consumed the hold -- if it
  // returns false (e.g. you weren't aiming at anything holdable), the tap still fires on release, so nothing is lost.
  // If null, E / right-click fire on PRESS exactly as before.
  onInteractHold: (() => boolean) | null = null;
  onRightInteractHold: (() => boolean) | null = null;
  onInteractRelease: (() => void) | null = null; // fires on E-release AFTER a consumed hold (release-to-select for hold wheels)
  private interactTimer = 0; private interactHeld = false; // timer handle + "the hold consumed this press"
  interactDownSince = 0; // performance.now() when E went down (0 = up) -- read-only: drives the HUD's hold-progress ring
  private rightTimer = 0; private rightTapHeld = false;
  onInventoryToggle: (() => void) | null = null;
  onCraftingToggle: (() => void) | null = null;
  onBuildToggle: (() => void) | null = null;
  onBuildWheel: (() => void) | null = null; // hold RIGHT MOUSE -> open the tool wheel (hammer/blueprint)
  onBuildWheelRelease: (() => void) | null = null; // release right mouse -> commit + close
  onRightInteract: (() => void) | null = null; // RIGHT MOUSE as a secondary contextual interact (idle only; main gates)
  onBuildHeight: (() => void) | null = null;
  onBuildAlt: (() => void) | null = null; // F while building -> the piece's alt action (flip / raise)
  onHotbarSelect: ((slot: number) => void) | null = null; // Digit1..Digit0 -> select hotbar slot (1..10)
  onHotbarCycle: ((dir: number) => void) | null = null;   // first-person scroll wheel: +1 next / -1 prev hotbar slot

  private middleHeld = false;
  private sprintToggled = false; // toggle-sprint mode: current run state (flipped by each Sprint tap)
  private firstPerson = false;
  private suppressed = false;
  private lookFrozen = false; // a first-person radial wheel is up: freeze camera-look so the mouse steers the wheel, not the view
  private overlayOpen = false; // inventory/storage/crafting up: pause movement + hotbar digits, keep toggles
  private readonly codeToAction = new Map<string, BindableAction>();
  private capture: { action: BindableAction; index: number; onComplete: (code: string | null) => void } | null = null;

  constructor(private readonly target: HTMLElement, private readonly settings: Settings) {
    this.rebuildBindings();
    window.addEventListener('keydown', (e) => this.onKey(e, true));
    window.addEventListener('keyup', (e) => this.onKey(e, false));
    target.addEventListener('pointerdown', (e) => this.onPointerDown(e));
    window.addEventListener('pointerup', (e) => this.onPointerUp(e));
    window.addEventListener('pointermove', (e) => this.onPointerMove(e));
    // FIRE TRIGGER via the raw button BITMASK. `pointerdown`/`pointerup` skip a button that is pressed/released WHILE
    // another is already held (mouse "chording") -- so pressing LEFT while right-mouse (ADS) is held never registered a
    // shot. `mousedown`/`mouseup` fire reliably per-button; deriving primaryHeld from `e.buttons` bit 0 makes left
    // register with right held (and vice versa). Idempotent with the pointerdown/up sets for a normal, single click.
    // A press that starts on interactive UI (the live hotbar in third person, panel chrome tagged
    // data-ui) must never register as the fire trigger; releases always clear the bit.
    const syncPrimary = (e: MouseEvent): void => {
      const down = (e.buttons & 1) !== 0;
      if (down && e.type === 'mousedown' && (e.target as HTMLElement | null)?.closest?.('[data-ui]')) return;
      this.state.primaryHeld = down;
    };
    window.addEventListener('mousedown', syncPrimary);
    window.addEventListener('mouseup', syncPrimary);
    target.addEventListener('wheel', (e) => this.onWheel(e), { passive: false });
    // Rebind capture for MOUSE buttons: capture-phase on window so a press is caught even over the settings
    // menu overlay (which sits above the canvas, so the canvas pointerdown below would never see it).
    window.addEventListener('pointerdown', (e) => this.onCapturePointer(e), { capture: true });
    // Cancel the native menu on the canvas too (the module-load window listener in main.ts is the real guarantee).
    // preventDefault ONLY -- capture-phase stopPropagation is unneeded and, in Firefox, makes the cancel order-fragile.
    target.addEventListener('contextmenu', (e) => e.preventDefault());
    window.addEventListener('blur', () => this.clearAll());
    // Safety net: whenever the pointer lock is released (leaving first person, opening the Esc menu, the
    // browser dropping it, etc.) make sure the OS cursor comes back. This is what fixes "3rd person keeps the
    // mouse hidden" -- the cursor is only ever hidden while the lock is actually held.
    document.addEventListener('pointerlockchange', () => {
      if (!document.pointerLockElement) this.target.style.cursor = 'default';
    });
  }

  // Rebuild the code -> action lookup after the keybindings change.
  rebuildBindings(): void {
    this.codeToAction.clear();
    for (const action of Object.keys(this.settings.binds) as BindableAction[]) {
      for (const code of this.settings.binds[action]) this.codeToAction.set(code, action);
    }
  }

  // While suppressed (menu open) gameplay input is ignored, but Esc and rebind capture work.
  setSuppressed(v: boolean): void {
    this.suppressed = v;
    if (v) this.clearAll();
  }

  // A gameplay overlay (inventory/storage/crafting) is open: stop walking + hotbar digit selection so
  // the player can't move or re-wield behind the panel, but leave the toggle keys (E/I/TAB) live so the
  // screen can still be closed. Distinct from setSuppressed, which fully pauses input for the Esc menu.
  setOverlayOpen(v: boolean): void {
    if (v === this.overlayOpen) return;
    this.overlayOpen = v;
    // Free the mouse for the UI when a panel opens (first person), re-lock for mouse-look when it closes.
    // Movement is NOT frozen (you can still run with a panel up); only mouse-look + hotbar digits pause.
    if (v) { this.clearAll(); this.releasePointerForOverlay(); }
    else this.relockForFirstPerson();
  }

  // Freeze/unfreeze camera look. Set while a first-person radial wheel is open: the pointer stays locked (so the
  // wheel's own pointermove handler keeps getting deltas to steer its virtual cursor) but the CAMERA no longer
  // turns from those deltas -- otherwise moving the mouse to pick a slice would also whip the view around.
  setLookFrozen(v: boolean): void { this.lookFrozen = v; }

  // First person: free mouse-look. Capture the pointer so mouse deltas drive the camera with no
  // button held, and aim from screen-center. Idempotent (only acts on a real change).
  setFirstPerson(v: boolean): void {
    if (v === this.firstPerson) return;
    this.firstPerson = v;
    if (v) {
      this.cursor.x = window.innerWidth / 2;
      this.cursor.y = window.innerHeight / 2;
      this.target.style.cursor = 'none'; // hide the cursor immediately, even before the lock engages
      // Try now (works if we still have the scroll's activation); the scroll handler and the next
      // click also re-request it, so the lock engages promptly.
      void this.target.requestPointerLock?.();
    } else {
      this.target.style.cursor = 'default'; // explicit (not '') so it shows even if a stale rule lingers
      if (document.pointerLockElement) document.exitPointerLock();
    }
  }

  // Free the pointer lock so an overlay (the tool wheel) gets a real, hoverable cursor. No-op outside
  // first person (where nothing was locked).
  releasePointerForOverlay(): void {
    if (document.pointerLockElement) document.exitPointerLock();
  }

  // Per-frame safety net: the pointer lock must be held ONLY in first person. If a stray lock lingers in third person
  // (e.g. a wheel gesture grabbed it, or first person was exited by some path), release it so the OS cursor returns
  // WITHOUT the player having to press Esc. The pointerlockchange listener then restores the cursor style. Cheap +
  // idempotent (guarded on actually holding the lock), so it is safe to call every frame from the loop.
  enforcePointerLock(): void {
    if (!this.firstPerson && document.pointerLockElement === this.target) document.exitPointerLock();
  }

  // Re-acquire the pointer lock after such an overlay closes, but only in first person where free
  // mouse-look needs it. Call from a real user gesture (e.g. the mouse-up that closed the overlay).
  relockForFirstPerson(): void {
    if (this.firstPerson && !this.suppressed && !this.lookFrozen && !document.pointerLockElement) {
      void this.target.requestPointerLock?.();
    }
  }

  // Capture the next key press to bind into action slot `index`. Escape cancels.
  beginCaptureRebind(action: BindableAction, index: number, onComplete: (code: string | null) => void): void {
    this.capture = { action, index, onComplete };
  }

  cancelCapture(): void {
    this.capture = null;
  }

  // Public: stop any held movement (used by the tab-hidden pause; blur already calls the private path).
  clearInput(): void { this.clearAll(); }

  // Called once per frame before movement is read. In AUTO sprint mode, force the run flag on so travel always
  // sprints (Shift is unused, so it's never held -> the Shift+right-click browser menu can't happen). Harmless
  // when standing still (velocity is zero anyway). Hold/toggle already track state.sprint through applyAction.
  applySprintMode(): void {
    if (this.settings.sprintMode === 'auto') this.state.sprint = !this.suppressed && !this.overlayOpen;
  }

  // A mouse press while a rebind is armed binds that button (code 'Mouse<n>', n = MouseEvent.button: 0 left,
  // 1 middle, 2 right, 3 back, 4 forward). The arming happens on the menu chip's CLICK (after its pointerup),
  // so the chip's own press is never self-captured. Keyboard rebind + Escape-cancel stay in onKey.
  private onCapturePointer(e: PointerEvent): void {
    if (!this.capture) return;
    const cap = this.capture;
    this.capture = null;
    const code = 'Mouse' + e.button;
    this.assignBind(cap.action, cap.index, code);
    cap.onComplete(code);
    e.preventDefault();
    e.stopPropagation();
  }

  private assignBind(action: BindableAction, index: number, code: string): void {
    // A key is never bound to two actions: strip it from every action first.
    for (const a of Object.keys(this.settings.binds) as BindableAction[]) {
      this.settings.binds[a] = this.settings.binds[a].filter((c) => c !== code);
    }
    const arr = this.settings.binds[action];
    if (index < arr.length) arr[index] = code;
    else arr.push(code);
    this.settings.binds[action] = arr.filter((c, i) => !!c && arr.indexOf(c) === i);
    saveSettings(this.settings);
    this.rebuildBindings();
  }

  private onKey(e: KeyboardEvent, down: boolean): void {
    // Rebind capture consumes the next keydown (Escape cancels it).
    if (down && this.capture) {
      e.preventDefault();
      const cap = this.capture;
      this.capture = null;
      if (e.code === 'Escape') {
        cap.onComplete(null);
        return;
      }
      this.assignBind(cap.action, cap.index, e.code);
      cap.onComplete(e.code);
      return;
    }

    if (e.code === 'Escape') {
      if (down && !e.repeat && this.onMenuToggle) this.onMenuToggle();
      e.preventDefault();
      return;
    }

    // Typing into a text field (rename box, add-user search, chat): never fire a gameplay bind, and never
    // preventDefault -- otherwise the character can't be typed AND a bound key (b/q/p...) pops a menu mid-word.
    // Gated on the DOWN edge only, so a key held BEFORE focusing the field still gets its keyup to clear state.
    if (down && typingInField()) return;

    if (this.suppressed) return; // menu open: gameplay input paused

    // First person: a keypress is a valid user-activation for pointer lock (plain mouse movement is not),
    // so the first movement key grabs the lock. NOT while an inventory/Tab overlay is open -- there the
    // cursor must stay free for the UI (no mouse-look). Views are toggled with V now, never the wheel.
    if (down && this.firstPerson && !this.overlayOpen && !this.lookFrozen && !document.pointerLockElement) {
      void this.target.requestPointerLock?.();
    }

    // Alt free-look is a fixed modifier, not part of the rebindable set.
    if (e.code === 'AltLeft' || e.code === 'AltRight') {
      this.state.altHeld = down;
      e.preventDefault();
      return;
    }

    const action = this.codeToAction.get(e.code);
    if (!action) return;
    e.preventDefault();

    this.applyAction(action, down, down && !e.repeat);
  }

  // Apply a bound action from EITHER the keyboard or a mouse button. `edge` is the press edge (down and not
  // a key-repeat) that one-shot toggles fire on; movement/hold actions just track `down`. Shared so a mouse
  // binding behaves exactly like the key binding, including the overlayOpen gating.
  private applyAction(action: BindableAction, down: boolean, edge: boolean): void {
    switch (action) {
      case 'debug': if (edge) this.onDebugToggle?.(); return;
      case 'interact':
        if (this.onInteractHold) { // opt-in hold/tap: tap fires on RELEASE; a >300ms hold fires onInteractHold once
          if (down) { if (edge) { this.interactHeld = false; this.interactDownSince = performance.now(); this.interactTimer = window.setTimeout(() => { this.interactHeld = this.onInteractHold?.() ?? false; }, 300); } }
          else { window.clearTimeout(this.interactTimer); this.interactDownSince = 0; if (!this.interactHeld) this.onInteract?.(); else this.onInteractRelease?.(); this.interactHeld = false; }
        } else if (edge) this.onInteract?.(); // no hold handler -> fire on press (snappy, unchanged)
        return;
      case 'inventory': if (edge) this.onInventoryToggle?.(); return;
      case 'crafting': if (edge) this.onCraftingToggle?.(); return;
      case 'build': if (edge) this.onBuildToggle?.(); return;
      case 'buildHeight': if (edge) this.onBuildHeight?.(); return;
      case 'buildAlt': if (edge) this.onBuildAlt?.(); return;
      // Hotbar 1..6 are rebindable now (default Digit1..Digit6). Suppressed while a panel is open, same as
      // the old hardcoded digit path. action is 'hotbar<n>' -> select slot n.
      case 'hotbar1': case 'hotbar2': case 'hotbar3': case 'hotbar4': case 'hotbar5': case 'hotbar6':
        if (edge && !this.overlayOpen) this.onHotbarSelect?.(Number(action.slice(6)));
        return;
    }
    // An inventory/Tab overlay is up: still allow MOVEMENT so you can run around with it open (mouse-look is
    // already off -- the pointer is freed for the UI), but suppress lean (a peek-aim, meaningless in a menu).
    if (this.overlayOpen && (action === 'leanLeft' || action === 'leanRight')) return;
    switch (action) {
      case 'forward': this.state.forward = down; break;
      case 'back': this.state.back = down; break;
      case 'left': this.state.left = down; break;
      case 'right': this.state.right = down; break;
      case 'sprint':
        // hold: run while held (classic). toggle: tap flips run on/off. auto: the key is a no-op (sprint is
        // forced on in applySprintMode). toggle+auto exist so you never HOLD Shift -> no Shift+right-click menu.
        if (this.settings.sprintMode === 'hold') this.state.sprint = down;
        else if (this.settings.sprintMode === 'toggle') { if (edge) { this.sprintToggled = !this.sprintToggled; this.state.sprint = this.sprintToggled; } }
        break;
      case 'crouch': this.state.crouch = down; break;
      case 'jump': this.state.jump = down; break;
      case 'leanLeft': this.state.leanLeft = down; break;
      case 'leanRight': this.state.leanRight = down; break;
    }
  }

  private onPointerDown(e: PointerEvent): void {
    // In first person, a click re-captures the pointer (e.g. after the menu released it).
    if (this.firstPerson && !this.suppressed && !document.pointerLockElement) {
      void this.target.requestPointerLock?.();
    }
    if (e.button === 1) {
      this.middleHeld = true;
      e.preventDefault();
    }
    if (e.button === 0) {
      this.state.primaryHeld = true;
      if (!this.firstPerson) {
        this.cursor.x = e.clientX;
        this.cursor.y = e.clientY;
      }
    }
    if (e.button === 2) {
      this.rightHeld = true;
      // A gun/bow is out: right mouse is TAP = interact/pickup, HOLD = aim-down-sights. Engage ADS only after a short
      // hold; a quick release is a tap (handled on pointer-up). The camera keeps moving throughout (see the move gate).
      this.rightGunPress = this.adsMode;
      if (this.rightGunPress) {
        this.rightDownAt = performance.now(); // aiming engages once the hold outlasts RIGHT_TAP_MS (see `aiming`)
        e.preventDefault();
        return;
      }
      if (!this.firstPerson) { this.cursor.x = e.clientX; this.cursor.y = e.clientY; } // aim the secondary-interact ray at the click point (like left-click does)
      // Hold RIGHT MOUSE to open the active tool's radial wheel (build wheel for the Blueprint,
      // upgrade/remove wheel for the Hammer). The handler is a no-op for other items, so right mouse
      // still does its normal job (e.g. AK aim-down-sights).
      if (!this.suppressed) this.onBuildWheel?.();
      // Right mouse is ALSO a secondary contextual interact when idle (open door/crate/chest, grab loot). Main
      // gates it so it only fires when not building/hammering/aiming (those keep wheel/ADS on right mouse).
      // Opt-in hold/tap (#7): with a hold handler set, the tap fires on RELEASE; a >300ms hold fires onRightInteractHold.
      if (!this.suppressed) {
        if (this.onRightInteractHold) { this.rightTapHeld = false; this.rightTimer = window.setTimeout(() => { this.rightTapHeld = this.onRightInteractHold?.() ?? false; }, 300); }
        else this.onRightInteract?.();
      }
      e.preventDefault();
    }
    // Side buttons (3 back / 4 forward): block the browser's history navigation and drive any bound action.
    if (e.button >= 3) {
      e.preventDefault();
      const mAction = this.codeToAction.get('Mouse' + e.button);
      if (mAction && !this.suppressed) this.applyAction(mAction, true, true);
    }
  }

  private onPointerUp(e: PointerEvent): void {
    if (e.button === 1) this.middleHeld = false;
    if (e.button === 0) this.state.primaryHeld = false;
    if (e.button === 2) this.releaseRight();
    if (e.button >= 3) {
      const mAction = this.codeToAction.get('Mouse' + e.button);
      if (mAction) this.applyAction(mAction, false, false);
    }
  }

  // The right-button release path, shared by the real pointerup and the pointermove self-heal below. A right
  // release can MISS pointerup entirely (button chording skips it, or a UI panel opening under the cursor
  // swallows it) -- rightHeld then sticks true and the look gate in onPointerMove freezes the camera until the
  // next right-click. Routing every release through here keeps the wheel/tap semantics in one place.
  private releaseRight(): void {
    this.rightHeld = false;
    if (this.rightGunPress) { // gun/bow out: HOLD = ADS. In THIRD person a quick TAP = interact/pickup (QoL helper);
      const tapped = performance.now() - this.rightDownAt < Input.RIGHT_TAP_MS; // in FIRST person right is aim-ONLY (interact is E, FPS-standard)
      this.rightGunPress = false;
      if (tapped && !this.suppressed && !this.firstPerson) this.onRightInteract?.();
      return;
    }
    this.onBuildWheelRelease?.(); // release commits + closes the wheel (no-op if none open)
    if (this.onRightInteractHold) { window.clearTimeout(this.rightTimer); if (!this.rightTapHeld && !this.suppressed) this.onRightInteract?.(); this.rightTapHeld = false; } // tap = release without a hold
  }

  private onPointerMove(e: PointerEvent): void {
    // RAW trigger state from the button bitmask. A LEFT press WHILE RIGHT is already held (ADS) does NOT fire a fresh
    // pointerdown/mousedown -- the browser reports it as a pointermove with buttons updated. Syncing here (before any
    // early-return) is what finally lets you fire while aiming. bit0 = left. Idempotent with pointerdown/up.
    this.state.primaryHeld = (e.buttons & 1) !== 0;
    // Self-heal a stuck right button the same way (bit1 = right): if the bitmask says released but no pointerup
    // arrived, run the release path now -- otherwise the look gate below stays frozen until the next right-click.
    if (this.rightHeld && (e.buttons & 2) === 0) this.releaseRight();
    if (this.firstPerson) {
      // Free mouse-look: every move turns the camera, no button held. Aim stays at screen center.
      this.cursor.x = window.innerWidth / 2;
      this.cursor.y = window.innerHeight / 2;
      if (this.suppressed || (this.rightHeld && !this.adsMode) || this.overlayOpen || this.lookFrozen) return; // freeze for a UI/wheel; a gun's right-hold (aim or tap) keeps looking
      const sens = this.settings.lookSensitivity * (this.aiming ? this.settings.adsSensitivity : 1); // sighted look has its own multiplier
      this.state.orbitDX += e.movementX * sens;
      this.state.orbitDY += e.movementY * sens;
      return;
    }
    this.cursor.x = e.clientX;
    this.cursor.y = e.clientY;
    if (this.suppressed || (this.rightHeld && !this.adsMode)) return; // right mouse drives the wheel (non-gun), not the camera; a gun's right-hold keeps orbiting
    if (this.middleHeld || this.state.altHeld) {
      const sens = this.settings.lookSensitivity;
      let dy = e.movementY;
      const invert = this.state.altHeld ? this.settings.invertAltY : this.settings.invertMmbY;
      if (invert) dy = -dy;
      this.state.orbitDX += e.movementX * sens;
      this.state.orbitDY += dy * sens;
    }
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    if (this.suppressed || this.overlayOpen) return; // menu/panel up: the wheel does nothing gameplay-wise
    // FIRST PERSON: the wheel cycles the hotbar (no zoom, no view change -- V toggles views now). Scroll DOWN
    // (deltaY > 0) advances to the next slot, scroll UP to the previous.
    if (this.firstPerson) { this.onHotbarCycle?.(e.deltaY > 0 ? 1 : -1); return; }
    // THIRD PERSON: the wheel zooms the camera distance (never crosses into first person -- see camera.zoomBy).
    this.state.zoomDelta += e.deltaY;
  }

  private clearAll(): void {
    const s = this.state;
    s.forward = s.back = s.left = s.right = false;
    s.sprint = s.crouch = s.jump = s.altHeld = false;
    this.sprintToggled = false; // drop toggle-sprint on blur/suppress so you don't resume auto-running unfocused
    s.leanLeft = s.leanRight = false;
    s.primaryHeld = false;
    this.middleHeld = false;
    this.rightHeld = false;
    this.rightGunPress = false; // drop any ADS/tap-in-progress on blur/suppress
    // #7: cancel any in-flight hold timer so a blur/suppress mid-press can't fire the hold callback while unfocused.
    // Mark the press CONSUMED (true) so a late key/mouse-up doesn't fire a spurious TAP either; the next press
    // resets the flag on its own edge. (Setting false here would let that stray up fire onInteract.)
    window.clearTimeout(this.interactTimer); this.interactHeld = true; this.interactDownSince = 0;
    window.clearTimeout(this.rightTimer); this.rightTapHeld = true;
  }

  consumeOrbit(): { dx: number; dy: number } {
    const dx = this.state.orbitDX;
    const dy = this.state.orbitDY;
    this.state.orbitDX = 0;
    this.state.orbitDY = 0;
    return { dx, dy };
  }

  consumeZoom(): number {
    const z = this.state.zoomDelta;
    this.state.zoomDelta = 0;
    return z;
  }
}
